import {
  abs,
  attribute,
  cameraViewMatrix,
  clamp,
  cross,
  Discard,
  dFdx,
  dFdy,
  dot,
  exp,
  Fn,
  float,
  floor,
  fract,
  If,
  ivec2,
  length,
  max,
  mix,
  mrt,
  normalize,
  output,
  packNormalToRGB,
  positionWorld,
  pow,
  reflect,
  smoothstep,
  sqrt,
  step,
  textureLoad,
  uniform,
  varyingProperty,
  vec2,
  vec3,
  vec4,
} from 'three/tsl';
import * as THREE from 'three/webgpu';
import { flags } from '../core/flags';
import { defaultDetail } from './mixStyles';
import { DETAIL_PERIOD, detailAt, type TerrainDetail } from './TerrainDetail';
import { MOUTH_CUT_LENGTH, RIM_SHADE, type TerrainMouth, WORN_SLOPE } from './TerrainMouth';
import { paletteHeight, type TerrainProfile } from './TerrainProfile';
import type { DrawNode, TerrainQuadtree } from './TerrainQuadtree';
import {
  bakeKey,
  noise3When,
  noiseWhen,
  type SurfaceContext,
  type TerrainStyle,
  vec3When,
} from './TerrainStyle';
import { BAKED_RES, TILE_RES, type TileGenerator } from './TileGenerator';
import type { WorldPanorama } from './WorldPanorama';

const GRID = 32; // quads per node edge
/**
 * The ground reaches this far (m) into a cave's entry cylinder before it is cut away. The
 * tunnel is a 28-sided tube, whose flat sides lie up to 2.5% of its radius inside the true
 * circle: with less overlap, thin gaps opened between the ground and the tunnel wall.
 */
const HOLE_OVERLAP = 0.06;
/** Depth of each node's skirt, as a share of the node's size (see the constructor). */
const SKIRT_DEPTH = 0.02;
/** The panorama's mip a lake reflects the land round it from (see the water's reflection). */
const SHORE_MIP = 4.5;
/** The components of a texel, by index (a baked atlas holds four noise terms). */
const CHANNELS = ['x', 'y', 'z', 'w'] as const;
/**
 * Nodes that come within this distance of the camera (m) draw with the detail textures, which
 * fade out by 220 m; the rest draw with a material without them. The detail's code slowed every
 * pixel it was compiled into, even those that sampled nothing (a bigger shader: fewer of them
 * run at once), by 2 ms for a screen of far side, which is most of what the player sees.
 */
const DETAIL_REACH = 230;
type N = any;

/** One instanced draw of nodes (and their skirts), with its own per-node data. */
interface TilePart {
  geometry: THREE.InstancedBufferGeometry;
  skirts: THREE.InstancedBufferGeometry | null;
  center: THREE.InstancedBufferAttribute;
  uv: THREE.InstancedBufferAttribute;
  tile: THREE.InstancedBufferAttribute;
}

const _anchorRel = new THREE.Vector3();

/** Upload an instance attribute's first n entries. */
/** A coordinate wrapped into [0, DETAIL_PERIOD). */
function wrapDetail(x: number): number {
  return ((x % DETAIL_PERIOD) + DETAIL_PERIOD) % DETAIL_PERIOD;
}

function upload(a: THREE.InstancedBufferAttribute, n: number): void {
  a.clearUpdateRanges();
  a.addUpdateRange(0, n * a.itemSize);
  a.needsUpdate = true;
}

/**
 * Draws a hollow's selected quadtree nodes with one instanced grid mesh (two with detail
 * textures: the nodes close by, which use them, are drawn apart; see DETAIL_REACH).
 *
 * Precision: per-instance data is the node centre relative to a terrain anchor
 * near the camera (computed in f64 on the CPU). The vertex shader builds each
 * vertex as an *offset* from that centre with cancellation-free formulas, so
 * vertices next to the camera are exact even in a 1,000 km hollow.
 */
export class TerrainRenderer {
  /** The ground (without detail textures); the nodes close by are a child mesh, `near`. */
  readonly mesh: THREE.Mesh;
  /** The nodes close by, with the detail textures (null without them). */
  readonly near: THREE.Mesh | null = null;
  /** Universe-space anchor (the mesh sits at anchor − camera). */
  readonly anchor = new THREE.Vector3();
  private readonly farPart: TilePart;
  private readonly nearPart: TilePart | null = null;
  private readonly cameraLocal = uniform(new THREE.Vector3());
  /** Seconds since start, for moving surfaces (water, lava). */
  readonly time = uniform(0);
  /** Average radiance of the sky that lakes reflect (set by the scene from its light rig). */
  readonly waterSky = uniform(new THREE.Color(0.12, 0.16, 0.24));
  /** The camera's universe position modulo DETAIL_PERIOD: where the detail textures' coordinates start. */
  private readonly detailOrigin = uniform(new THREE.Vector3());
  readonly maxInstances: number;

  constructor(
    private readonly gen: TileGenerator,
    private readonly tree: TerrainQuadtree,
    private readonly hollowCenter: THREE.Vector3,
    profile: TerrainProfile,
    style: TerrainStyle,
    lights: THREE.LightsNode | null,
    maxInstances = 6000,
    /** Cave mouths: the tunnel entry cylinders are cut out of the ground. */
    private readonly mouths: TerrainMouth[] = [],
    /** Tell the palette each pixel's footprint, so it fades noise finer than a pixel (see SurfaceContext). */
    private readonly filterPalette = false,
    /** Real textures for the ground close by (none: the palette's noise alone). */
    detail: TerrainDetail | null = null,
    /** The far wall, for the lakes to reflect (none: they reflect a flat sky colour). */
    private readonly panorama: WorldPanorama | null = null,
  ) {
    this.maxInstances = maxInstances;
    const verts = (GRID + 1) * (GRID + 1);
    const pos = new Float32Array(verts * 3);
    for (let j = 0; j <= GRID; j++) {
      for (let i = 0; i <= GRID; i++) {
        const k = (j * (GRID + 1) + i) * 3;
        pos[k] = i / GRID;
        pos[k + 1] = j / GRID;
      }
    }
    const idx: number[] = [];
    for (let j = 0; j < GRID; j++) {
      for (let i = 0; i < GRID; i++) {
        const a = j * (GRID + 1) + i;
        const b = a + 1;
        const c = a + GRID + 1;
        const d = c + 1;
        // Counter-clockwise seen from inside the hollow (normals face the centre).
        idx.push(a, c, b, b, c, d);
      }
    }
    // Skirts: each border vertex again, flagged (z = 1) to hang down into the rock, joined
    // to the border in a strip facing out of the node. Neighbouring nodes meet only to float
    // precision, overlap by a hair on slopes that leaves a slit at grazing angles, and while
    // tiles stream a node can border one two levels coarser (its edge then isn't morphed to
    // match): any such crack showed the background. Through it the neighbour's skirt shows
    // instead, lying in the rock, so it's never seen otherwise. ?skirts=0 leaves them out.
    const ring: number[] = [];
    for (let i = 0; i < GRID; i++) ring.push(i); // j = 0, i rising
    for (let j = 0; j < GRID; j++) ring.push(j * (GRID + 1) + GRID); // i = GRID, j rising
    for (let i = GRID; i > 0; i--) ring.push(GRID * (GRID + 1) + i); // j = GRID, i falling
    for (let j = GRID; j > 0; j--) ring.push(j * (GRID + 1)); // i = 0, j falling
    const allPos = new Float32Array((verts + ring.length) * 3);
    allPos.set(pos);
    ring.forEach((v, k) => {
      allPos.set([pos[v * 3], pos[v * 3 + 1], 1], (verts + k) * 3);
    });
    const skirtIdx: number[] = [];
    for (let k = 0; k < ring.length; k++) {
      const b0 = ring[k];
      const b1 = ring[(k + 1) % ring.length];
      const s0 = verts + k;
      const s1 = verts + ((k + 1) % ring.length);
      skirtIdx.push(b0, b1, s1, b0, s1, s0);
    }
    const position = new THREE.BufferAttribute(allPos, 3);
    const index = new THREE.Uint16BufferAttribute(idx, 1);
    const skirtIndex = new THREE.Uint16BufferAttribute(skirtIdx, 1);
    const skirtsOn = flags.bool('skirts', true);
    const part = (): TilePart => {
      const center = new THREE.InstancedBufferAttribute(new Float32Array(maxInstances * 3), 3);
      const uv = new THREE.InstancedBufferAttribute(new Float32Array(maxInstances * 4), 4);
      const tile = new THREE.InstancedBufferAttribute(new Float32Array(maxInstances * 4), 4);
      for (const a of [center, uv, tile]) a.setUsage(THREE.DynamicDrawUsage);
      const instanced = (ix: THREE.BufferAttribute) => {
        const g = new THREE.InstancedBufferGeometry();
        g.setAttribute('position', position);
        g.setIndex(ix);
        g.setAttribute('iCenter', center);
        g.setAttribute('iUV', uv);
        g.setAttribute('iTile', tile);
        g.instanceCount = 0;
        g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e9);
        return g;
      };
      return {
        geometry: instanced(index),
        skirts: skirtsOn ? instanced(skirtIndex) : null,
        center,
        uv,
        tile,
      };
    };
    const meshes = (p: TilePart, mat: THREE.MeshStandardNodeMaterial, name: string) => {
      if (lights) mat.lightsNode = lights;
      const mesh = new THREE.Mesh(p.geometry, mat);
      mesh.frustumCulled = false;
      mesh.name = name;
      // The skirts are drawn after everything else, so all but the few showing through a crack
      // fail the depth test before any shading: at grazing angles they span much of the screen.
      let skirts: THREE.Mesh | null = null;
      if (p.skirts) {
        skirts = new THREE.Mesh(p.skirts, mat);
        skirts.frustumCulled = false;
        skirts.renderOrder = 1;
        skirts.name = `${name} skirts`;
      }
      return { mesh, skirts };
    };

    this.farPart = part();
    const far = meshes(this.farPart, this.buildMaterial(profile, style, null), 'terrain');
    this.mesh = far.mesh;
    if (far.skirts) this.mesh.add(far.skirts);
    if (detail) {
      this.nearPart = part();
      const near = meshes(this.nearPart, this.buildMaterial(profile, style, detail), 'terrain close by');
      this.near = near.mesh;
      this.mesh.add(near.mesh);
      if (near.skirts) this.mesh.add(near.skirts);
    }
  }

  /** Mesh cells per root-tile edge at a level: its vertices sit on every other texel. */
  cellsAt(level: number): number {
    return GRID * 2 ** level;
  }

  /** Refresh instances from the quadtree's draw list. Call after select(). */
  update(cameraUniverse: THREE.Vector3, dt = 0): void {
    this.time.value += dt;
    // Rebase the anchor when the camera wanders more than 2 km from it.
    if (this.anchor.distanceToSquared(cameraUniverse) > 2000 * 2000 || this.anchor.lengthSq() === 0) {
      this.anchor.copy(cameraUniverse);
    }
    _anchorRel.subVectors(this.hollowCenter, this.anchor); // hollow centre relative to anchor
    this.cameraLocal.value.subVectors(cameraUniverse, this.anchor);
    this.detailOrigin.value.set(
      wrapDetail(cameraUniverse.x),
      wrapDetail(cameraUniverse.y),
      wrapDetail(cameraUniverse.z),
    );
    const list = this.tree.drawList;
    const nearPart = this.nearPart;
    let nFar = 0;
    let nNear = 0;
    for (let i = 0; i < list.length; i++) {
      const d = list[i];
      if (nearPart && d.dist < DETAIL_REACH) {
        if (nNear < this.maxInstances) this.write(nearPart, nNear++, d);
      } else if (nFar < this.maxInstances) {
        this.write(this.farPart, nFar++, d);
      }
    }
    this.finish(this.farPart, nFar);
    if (nearPart) this.finish(nearPart, nNear);
  }

  /** Node `d`'s instance data, at index i of a part. */
  private write(p: TilePart, i: number, d: DrawNode): void {
    const c = p.center.array as Float32Array;
    const uv = p.uv.array as Float32Array;
    const t = p.tile.array as Float32Array;
    const node = d.node;
    // In plain numbers: through Vector3's shared methods every read boxed a number.
    const pt = node.point;
    c[i * 3] = pt.x + _anchorRel.x;
    c[i * 3 + 1] = pt.y + _anchorRel.y;
    c[i * 3 + 2] = pt.z + _anchorRel.z;
    const size = 2 / 2 ** node.level;
    uv[i * 4] = -1 + (node.x + 0.5) * size;
    uv[i * 4 + 1] = -1 + (node.y + 0.5) * size;
    uv[i * 4 + 2] = size;
    uv[i * 4 + 3] = node.face;
    t[i * 4] = this.gen.slotX(node.slot);
    t[i * 4 + 1] = this.gen.slotY(node.slot);
    t[i * 4 + 2] = d.morphStart;
    t[i * 4 + 3] = d.morphEnd;
  }

  /** Draw a part's first n instances, uploading only those. */
  private finish(p: TilePart, n: number): void {
    p.geometry.instanceCount = n;
    if (p.skirts) p.skirts.instanceCount = n;
    if (n === 0) return;
    upload(p.center, n);
    upload(p.uv, n);
    upload(p.tile, n);
  }

  private buildMaterial(
    profile: TerrainProfile,
    style: TerrainStyle,
    detail: TerrainDetail | null,
  ): THREE.MeshStandardNodeMaterial {
    const atlas = this.gen.atlas;
    const biomeAtlas = this.gen.biomeAtlas;
    const surfaceAtlas = this.gen.surfaceAtlas;
    const shape = this.tree.shape.gpu;
    const cameraLocal = this.cameraLocal;

    const iC = attribute('iCenter', 'vec3');
    const iUV = attribute('iUV', 'vec4');
    const iT = attribute('iTile', 'vec4');

    /**
     * Bilinear fetch of a tile at grid coordinate g ∈ [0,1]² (no sampler needed). The slot
     * comes interpolated (vSlot), so it is rounded: a hair under an integer, truncated, read
     * the next slot over on the tile's first column, and some other tile's ridges, relief and
     * slope drew dotted lines along tile borders.
     */
    const sampleTile = (slot: N, g: N, tex: N = atlas) => {
      const t = g.mul(TILE_RES - 1);
      const t0 = clamp(floor(t), vec2(0), vec2(TILE_RES - 2));
      const f = t.sub(t0);
      const base = slot.add(t0).add(0.5);
      const a = textureLoad(tex, ivec2(base));
      const b = textureLoad(tex, ivec2(base.add(vec2(1, 0))));
      const c = textureLoad(tex, ivec2(base.add(vec2(0, 1))));
      const d = textureLoad(tex, ivec2(base.add(vec2(1, 1))));
      return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
    };

    const vDir = varyingProperty('vec3', 'vDir');
    const vLocal = varyingProperty('vec2', 'vTileLocal');
    const vSlot = varyingProperty('vec2', 'vSlot');
    /** Bilinear fetch of a tile's baked noise (half resolution: see TileGenerator.bakedAtlases). */
    const sampleBaked = (slot: N, g: N, tex: N) => {
      const origin = floor(slot.div(TILE_RES).add(0.5)).mul(BAKED_RES);
      const t = g.mul(BAKED_RES - 1);
      const t0 = clamp(floor(t), vec2(0), vec2(BAKED_RES - 2));
      const f = t.sub(t0);
      const base = origin.add(t0).add(0.5);
      const a = textureLoad(tex, ivec2(base));
      const b = textureLoad(tex, ivec2(base.add(vec2(1, 0))));
      const c = textureLoad(tex, ivec2(base.add(vec2(0, 1))));
      const d = textureLoad(tex, ivec2(base.add(vec2(1, 1))));
      return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
    };
    const vH = varyingProperty('float', 'vHeight');
    const vHollow = varyingProperty('vec3', 'vHollowPos');
    // 0 on the ground, rising to 1 down a skirt.
    const vSkirt = varyingProperty('float', 'vSkirt');
    // The shape's gradient axes (constant over a sphere's face, varying over a torus).
    const vAxisU = varyingProperty('vec3', 'vAxisU');
    const vAxisV = varyingProperty('vec3', 'vAxisV');
    const vAxisN = varyingProperty('vec3', 'vAxisN');

    const position = Fn(() => {
      const g = attribute('position', 'vec3').xy;
      const uc = iUV.x;
      const vc = iUV.y;
      const size = iUV.z;
      const slot = iT.xy;
      // Nodes compute shared edge vertices independently, so they agree only to float
      // precision; growing each node's footprint by 0.1% makes neighbours (and T-junctions) overlap by a
      // hair and closes sub-pixel pinholes. Heights still use the true grid.
      const surf = (g0: N) =>
        shape.at(
          iUV.w,
          uc,
          vc,
          size,
          g0
            .sub(0.5)
            .mul(1 + 1e-3)
            .add(0.5),
        );

      // Height-only CDLOD morph. Vertices keep their (u, v); odd vertices blend their
      // height toward the coarse mesh's edge/diagonal average (from the parent-band
      // heights). Unlike sliding vertices, this doesn't depend on grid direction, so
      // edges shared across cube faces stay watertight at every morph amount.
      // On a sphere the coarse mesh's edges are straight chords, so odd vertices morph
      // their full 3D position toward the chord midpoint of the coarse endpoints
      // (parent-band heights). Even vertices morph toward their own parent-band height.
      const s = surf(g);
      const vi = g.mul(GRID); // vertex index (integer-valued)
      const texel = vi.mul(2); // mesh vertices sit on even texels
      const fine = textureLoad(atlas, ivec2(slot.add(texel)));
      const odd = fract(vi.mul(0.5)).mul(2); // 1 where the index is odd
      const both = odd.x.mul(odd.y);
      const offA = vec2(odd.x.negate().add(both.mul(2)), odd.y.negate());
      const hA = textureLoad(atlas, ivec2(slot.add(texel.add(offA.mul(2))))).y;
      const hB = textureLoad(atlas, ivec2(slot.add(texel.sub(offA.mul(2))))).y;
      const sA = surf(g.add(offA.div(GRID)));
      const sB = surf(g.sub(offA.div(GRID)));
      const pA = iC.add(sA.offset).sub(sA.normal.mul(max(hA, 0)));
      const pB = iC.add(sB.offset).sub(sB.normal.mul(max(hB, 0)));
      const pFine = iC.add(s.offset).sub(s.normal.mul(max(fine.x, 0)));
      const pCoarse = pA.add(pB).mul(0.5);
      const dist = length(pFine.sub(cameraLocal));
      const k = clamp(dist.sub(iT.z).div(iT.w.sub(iT.z)), 0, 1);
      const hRaw = mix(fine.x, hA.add(hB).mul(0.5), k);
      // Skirt vertices (z = 1) hang into the rock (the normal points away from the centre).
      const skirt = attribute('position', 'vec3').z.mul(
        size.mul(this.tree.shape.rootArc * 0.5 * SKIRT_DEPTH),
      );
      const pos = mix(pFine, pCoarse, k).add(s.normal.mul(skirt));
      vDir.assign(s.normal);
      vAxisU.assign(s.U);
      vAxisV.assign(s.V);
      vAxisN.assign(s.N);
      vLocal.assign(g);
      vSlot.assign(slot);
      vH.assign(hRaw);
      // A skirt is coloured as the ground at its top edge (it shows only through cracks):
      // its palette point stays on the edge, not down in the rock.
      vHollow.assign(s.local.sub(s.normal.mul(max(hRaw, 0))));
      vSkirt.assign(attribute('position', 'vec3').z);
      return pos;
    });

    const mat = new THREE.MeshStandardNodeMaterial({ roughness: 0.9, metalness: 0 });
    mat.positionNode = position();

    const surface = () => {
      const dir = normalize(vDir);
      const Nf = normalize(vAxisN);
      const Uf = normalize(vAxisU);
      const Vf = normalize(vAxisV);
      const tile = sampleTile(vSlot, vLocal);
      const gU = tile.z;
      const gV = tile.w;
      const gN = gU
        .mul(dot(dir, Uf))
        .add(gV.mul(dot(dir, Vf)))
        .negate()
        .div(dot(dir, Nf));
      const G = Uf.mul(gU).add(Vf.mul(gV)).add(Nf.mul(gN));
      const up = dir.negate();
      const isWater = vH.lessThan(0);
      const nMacro = normalize(up.sub(G));
      const slopeTile = float(1).sub(dot(nMacro, up));
      // Heightmap cliffs can fall between texels, where the tile gradient underestimates
      // them; the rendered triangle's own normal (screen-space derivatives) catches those.
      const dpx = dFdx(positionWorld);
      const dpy = dFdy(positionWorld);
      // Its length is the area of ground the pixel covers.
      const pixelArea = cross(dpx, dpy);
      const nGeo = normalize(pixelArea);
      const slopeGeo = float(1).sub(dot(nGeo, up).abs());
      // Only where the tiles already slope: sliver triangles at LOD seams on flat ground
      // look steep too, and would draw rock lines across meadows. Never on a skirt: it is a
      // wall, and would colour the bits of it showing through cracks as cliffs.
      const slope = max(
        slopeTile,
        slopeGeo
          .mul(smoothstep(0.38, 0.6, slopeGeo))
          .mul(smoothstep(0.1, 0.2, slopeTile))
          .mul(float(1).sub(step(1e-6, vSkirt))),
      );
      // Heights as the palette sees them (a shrunk world's are its big world's); water depth too.
      const h = paletteHeight(profile, vH);
      const p = vHollow;
      const dist = length(positionWorld);
      // Close-up detail fades out before it can alias.
      const near = float(1).sub(smoothstep(25, 220, dist));
      const mid = float(1).sub(smoothstep(300, 2500, dist));
      // Ground this pixel spans (m): the side of a square of the same area. At grazing angles
      // the footprint is long and thin, and its long axis would blur away patterns the short
      // one still resolves (forest patches toward the horizon). Taken from the derivatives
      // above, before the palette's noise branches, which only read it.
      const footprint = this.filterPalette ? max(sqrt(length(pixelArea)), 1e-4) : undefined;
      // Cave mouths: the lip is worn bare (WORN_SLOPE) and darkens a little toward the hole. The
      // tunnel's first metres carry on with the same palette. Its edge is soft and ragged at two
      // sizes: a sharp, smooth one drew a stain round every mouth.
      let worn: N = float(0);
      let shade: N = float(1);
      for (const m of this.mouths) {
        const w = p.sub(vec3(m.point.x, m.point.y, m.point.z));
        const ax = vec3(m.axis.x, m.axis.y, m.axis.z);
        const along = dot(w, ax);
        const perp = length(w.sub(ax.mul(along)));
        const here = float(1).sub(step(MOUTH_CUT_LENGTH, abs(along)));
        const ragged = noiseWhen(p.mul(1 / 6), here)
          .mul(2.5)
          .add(noiseWhen(p.mul(1 / 1.7), here).mul(0.6));
        worn = max(
          worn,
          float(1)
            .sub(smoothstep(m.radius + m.lipWidth * 0.2, m.radius + m.lipWidth + 1, perp.add(ragged)))
            .mul(here),
        );
        shade = shade.mul(
          mix(
            float(1),
            mix(float(RIM_SHADE), float(1), smoothstep(m.radius, m.radius + m.lipWidth * 0.6, perp)),
            here,
          ),
        );
      }
      const paletteSlope = this.mouths.length > 0 ? max(slope, worn.mul(WORN_SLOPE)) : slope;
      // The palette's broad noise terms, carried by the tiles (TerrainStyle.bakedNoise).
      const bakedList = this.gen.baked;
      let baked: Map<string, N> | undefined;
      // ?bake=0: every term per pixel again (for comparisons).
      if (bakedList.length > 0 && this.gen.bakedAtlases.length > 0 && flags.bool('bake', true)) {
        const texels = this.gen.bakedAtlases.map((t) => sampleBaked(vSlot, vLocal, t).mul(2).sub(1));
        const map = new Map<string, N>();
        for (const [i, b] of bakedList.entries()) {
          map.set(bakeKey(b.wavelength, b.offset), texels[i >> 2][CHANNELS[i & 3]]);
        }
        baked = map;
      }
      const ctx: SurfaceContext = {
        p,
        h,
        slope: paletteSlope,
        up,
        dist,
        near,
        mid,
        footprint,
        biome: biomeAtlas ? sampleTile(vSlot, vLocal, biomeAtlas) : undefined,
        ridge: surfaceAtlas ? sampleTile(vSlot, vLocal, surfaceAtlas).xyz.mul(2).sub(1) : undefined,
        baked,
        profile,
        time: this.time,
      };

      // Micro-normals close by, without detail textures (whose normal maps give the close-up
      // relief otherwise): vector noise in the tangent plane (grass tufts, rock grit), scaled
      // by slope so cliffs read rougher than meadows, in a real branch. (A 7 m one out to 2.5 km
      // cost 0.4 ms at 1440p and, lit from overhead, showed nowhere.)
      let bumpT: N = vec3(0);
      if (!detail) {
        const bump = noise3When(p.mul(1 / 0.9), near).mul(near.mul(0.22));
        bumpT = bump.sub(nMacro.mul(dot(bump, nMacro))).mul(slope.mul(1.5).add(0.6));
      }
      const land = style.land(ctx);
      let landAlbedo: N = land.albedo;
      let nBase: N = nMacro;
      if (detail) {
        // Real textures close by: their light and dark, and their relief, over the palette.
        const d = detailAt(
          detail,
          positionWorld.add(this.detailOrigin),
          nMacro,
          land.detail ?? defaultDetail(slope),
          near.mul(isWater.select(float(0), float(1))),
        );
        landAlbedo = landAlbedo.mul(d.albedo);
        nBase = d.normal;
      }
      const nLand = normalize(nBase.add(bumpT));
      if (this.mouths.length > 0) landAlbedo = landAlbedo.mul(shade);

      // Water: two drifting wave octaves tilt the normal (calming with distance so far
      // lakes don't shimmer), Fresnel mixes the body colour with the sky it reflects, and
      // a thin foam line marks the shore.
      const t = this.time;
      const wet = float(1).sub(step(0, vH));
      // Each octave fades out before its ripples shrink below a pixel (they'd sparkle).
      const w1Weight = float(1)
        .sub(smoothstep(80, 700, dist))
        .mul(wet);
      const w2Weight = float(1)
        .sub(smoothstep(15, 120, dist))
        .mul(wet);
      const w1 = noise3When(p.mul(1 / 7).add(vec3(t.mul(0.3), t.mul(0.1), t.mul(-0.2))), w1Weight).mul(
        w1Weight.mul(0.1),
      );
      const w2 = noise3When(p.mul(1 / 1.6).add(vec3(t.mul(-0.2), t.mul(0.5), t.mul(0.35))), w2Weight).mul(
        w2Weight.mul(0.045),
      );
      const wave = w1.add(w2);
      const nWater = normalize(up.add(wave.sub(up.mul(dot(wave, up)))));
      const view = normalize(positionWorld.negate());
      const cosV = clamp(dot(nWater, view), 0, 1);
      const fresnel = float(0.02).add(pow(float(1).sub(cosV), 5).mul(0.98));
      const ws = style.water;
      const depth = h.negate();
      let body: N = mix(vec3(...ws.shallow), vec3(...ws.deep), float(1).sub(exp(depth.div(-ws.depthScale))));
      // Foam: broken up by noise close by, a soft averaged band further away.
      const foamN = noiseWhen(p.mul(1 / 2.5).add(vec3(0, t.mul(0.3), 0)), near.mul(wet)).mul(near);
      const foam = float(1)
        .sub(smoothstep(0.15, 0.8, depth.add(foamN.mul(0.3))))
        .mul(near.mul(0.6).add(0.4))
        .mul(mid);
      body = mix(body, vec3(...(ws.foam ?? [0.78, 0.84, 0.84])), foam.mul(0.55));
      // What the water reflects: the far wall where the reflected ray meets it (the reference
      // sphere through this point), from the world's panorama. Until it is drawn, the flat sky
      // colour.
      const sky = vec3(this.waterSky as N);
      const pano = this.panorama;
      const reflected: N = pano
        ? vec3When(() => {
            const rDir = reflect(view.negate(), nWater);
            const along = dot(p, rDir);
            const hit = normalize(p.add(rDir.mul(max(along.mul(-2), 0))));
            const rise = smoothstep(0.02, 0.12, along.negate().div(length(p)));
            // A ray that barely rises meets the land round the lake before the far wall: it
            // reflects the panorama's colours there, blurred to their average (the flat sky colour
            // drew grazing views of every lake as a pale grey sheet in a world of green hills).
            const land = pano.sample(hit, SHORE_MIP);
            // Rough surfaces (lava, slush) blur it: from mip 1 for calm water.
            const far = pano.sample(hit, 1 + (ws.roughness ?? 0.06) * 5);
            return mix(sky, mix(land, far, rise), pano.ready);
          }, wet)
        : sky;
      const skyRefl = reflected.mul(fresnel).mul(float(1).sub(foam));

      const n: N = isWater.select(nWater, nLand);
      // Water and land are blended by `wet` (0 or 1), not selected: each select() became an
      // if/else that built its arms where they stood, so three of them built the land palette
      // three times over (a world of several biomes computed its whole palette three times a
      // pixel). One if/else for all three was no better: the normal, built later, read values
      // left inside its arms, unset for the other kind of ground.
      const albedo: N = mix(landAlbedo, body.mul(float(1).sub(fresnel)), wet);
      const rough: N = mix(land.roughness, mix(float(ws.roughness ?? 0.06), float(0.6), foam), wet);
      let emissive: N = skyRefl.mul(wet);
      if (land.emissive) emissive = emissive.add(land.emissive.mul(float(1).sub(wet)));
      if (ws.emissive) emissive = emissive.add(vec3When(() => ws.emissive!(ctx), wet));
      // For ambient occlusion: the ground's own shape, without the detail textures' relief.
      const nShape: N = isWater.select(up, nMacro);
      return { albedo, rough, n, emissive, nShape };
    };

    const s = surface();
    // Cave mouths: cut each tunnel's entry cylinder out of the ground.
    const holes = this.mouths.map((m) => ({
      point: vec3(m.point.x, m.point.y, m.point.z),
      axis: vec3(m.axis.x, m.axis.y, m.axis.z),
      radius: m.radius,
    }));
    mat.colorNode =
      holes.length === 0
        ? vec4(s.albedo, 1)
        : Fn(() => {
            for (const m of holes) {
              const w = vHollow.sub(m.point);
              const t = dot(w, m.axis);
              const perp = length(w.sub(m.axis.mul(t)));
              If(
                abs(t)
                  .lessThan(MOUTH_CUT_LENGTH)
                  .and(perp.lessThan(m.radius - HOLE_OVERLAP)),
                () => {
                  Discard();
                },
              );
            }
            return vec4(s.albedo, 1);
          })();
    mat.roughnessNode = s.rough;
    mat.normalNode = normalize(cameraViewMatrix.mul(vec4(s.n, 0)).xyz);
    // Ambient occlusion reads the ground's shape, not the detail textures' relief: it took the
    // normal-mapped pebbles of the sand (a texture that repeats every 2 m) for occluders, and
    // dotted bare ground with dark specks in rows.
    mat.mrtNode = mrt({
      output,
      normal: packNormalToRGB(normalize(cameraViewMatrix.mul(vec4(s.nShape, 0)).xyz)),
    });
    mat.emissiveNode = s.emissive;
    return mat;
  }
}
